import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function SettingsScreen() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/settings");
        setForm(data);
      } catch {}
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put("/settings", {
        journal_retention_days: +form.journal_retention_days,
        free_scan_limit: +form.free_scan_limit,
        premium_scan_limit: +form.premium_scan_limit,
        maintenance_mode: !!form.maintenance_mode,
        min_app_version: form.min_app_version,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed (super admin required)"); }
    finally { setBusy(false); }
  };

  if (!form) {
    return <AdminLayout><div className="p-10 text-center"><span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-shadow-green mb-1">System Settings</h1>
      <p className="text-muted-soft text-sm mb-6">App-wide configuration (super admin only).</p>

      <div className="glass-card p-6 max-w-2xl space-y-4" data-testid="settings-form">
        <Row label="Journal retention (days)" hint="Platinum users' journal data auto-deletes after this many days.">
          <input type="number" min={1} max={30} className="glass-input max-w-[120px]" value={form.journal_retention_days} onChange={(e) => setForm({ ...form, journal_retention_days: e.target.value })} data-testid="set-retention" />
        </Row>
        <Row label="Free tier scan limit / day" hint="Max identifications + diagnoses per 24h for free users.">
          <input type="number" min={1} className="glass-input max-w-[120px]" value={form.free_scan_limit} onChange={(e) => setForm({ ...form, free_scan_limit: e.target.value })} data-testid="set-free-limit" />
        </Row>
        <Row label="Premium tier scan limit / day" hint="Max for Premium users (Platinum is unlimited).">
          <input type="number" min={1} className="glass-input max-w-[120px]" value={form.premium_scan_limit} onChange={(e) => setForm({ ...form, premium_scan_limit: e.target.value })} data-testid="set-prem-limit" />
        </Row>
        <Row label="Minimum app version" hint="Older clients are forced to update.">
          <input className="glass-input max-w-[160px]" value={form.min_app_version} onChange={(e) => setForm({ ...form, min_app_version: e.target.value })} data-testid="set-version" />
        </Row>
        <Row label="Maintenance mode" hint="Block all non-admin traffic while you ship.">
          <button
            onClick={() => setForm({ ...form, maintenance_mode: !form.maintenance_mode })}
            className={`relative w-11 h-6 rounded-full ${form.maintenance_mode ? "bg-[#D32F2F]" : "bg-white/15"}`}
            style={form.maintenance_mode ? { boxShadow: "var(--glow-red)" } : {}}
            data-testid="set-maintenance"
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.maintenance_mode ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </Row>

        <button onClick={save} disabled={busy} className="btn-primary mt-4 inline-flex items-center gap-2" data-testid="save-settings">
          <Save size={14} /> {busy ? "Saving…" : "Save settings"}
        </button>
      </div>
    </AdminLayout>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-b-0">
      <div className="flex-1">
        <p className="text-sm font-semibold">{label}</p>
        {hint && <p className="text-[11px] text-muted-soft mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}
